import { forwardRef } from 'react'

import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  Validate,
  ValidateResult,
} from 'react-hook-form'

type FieldRenderProps = keyof ControllerRenderProps<FieldValues, string>
type FieldControllerProps<T extends React.ElementType> = {
  FieldComponent: T
  /**
   * Field render props are props that are passed by react-hook-form's Controller to the field component so should not be required
   */
  componentProps?: Partial<Pick<React.ComponentProps<T>, FieldRenderProps>> &
    Omit<React.ComponentProps<T>, FieldRenderProps>
  /**
   * Transform function to apply to the input value
   */
  transform?: (value: string) => unknown
} & Omit<React.ComponentProps<typeof Controller>, 'render'>

function FieldControllerInner<T extends React.ElementType>(
  { FieldComponent, componentProps, transform, ...controllerProps }: FieldControllerProps<T>,
  ref: React.Ref<HTMLElement>
) {
  const { control } = useFormContext()

  const rulesWithTransform =
    transform && controllerProps.rules
      ? {
          ...controllerProps.rules,
          validate:
            typeof controllerProps.rules.validate === 'function'
              ? (((value: unknown): ValidateResult | Promise<ValidateResult> => {
                  const transformedValue = typeof value === 'string' ? transform(value) : value
                  return (controllerProps.rules?.validate as Validate<unknown>)(transformedValue)
                }) as Validate<unknown>)
              : typeof controllerProps.rules.validate === 'object' && controllerProps.rules.validate
              ? (Object.fromEntries(
                  Object.entries(controllerProps.rules.validate).map(([key, validator]) => [
                    key,
                    ((value: unknown): ValidateResult | Promise<ValidateResult> => {
                      const transformedValue = typeof value === 'string' ? transform(value) : value
                      return validator(transformedValue)
                    }) as Validate<unknown>,
                  ])
                ) as Record<string, Validate<unknown>>)
              : undefined,
        }
      : controllerProps.rules

  const transformedDefaultValue =
    transform && controllerProps.defaultValue && typeof controllerProps.defaultValue === 'string'
      ? transform(controllerProps.defaultValue)
      : controllerProps.defaultValue || componentProps?.['defaultValue']

  return (
    <Controller
      {...controllerProps}
      rules={rulesWithTransform}
      control={control}
      defaultValue={transformedDefaultValue}
      render={({ field, fieldState: { error, isDirty }, formState: { isSubmitted, isSubmitting } }) => {
        const { onBlur, onChange, value = '' } = field
        const showError = !!error && (error?.type !== 'required' || isSubmitted)

        return (
          <FieldComponent
            {...({
              ...field,
              ...componentProps,
              value: componentProps?.['value'] ?? value,
              disabled: componentProps?.['disabled'] || isSubmitting,
              errorText: error?.message,
              id: componentProps?.['id'] ?? field.name,
              isInvalid: componentProps?.['isInvalid'] || showError,
              onBlur: (e: React.FocusEvent<HTMLElement>) => {
                componentProps?.['onBlur']?.(e)
                if (isDirty) {
                  // validate onBlur only when field is dirty
                  onBlur()
                }
              },
              onChange: (e: React.ChangeEvent<HTMLElement>) => {
                if (transform && 'target' in e && 'value' in e.target) {
                  const inputValue = e.target.value as string
                  const transformedValue = transform(inputValue)
                  onChange(transformedValue)
                  componentProps?.['onChange']?.(e)
                } else {
                  onChange(e)
                  componentProps?.['onChange']?.(e)
                }
              },
              ref,
            } as React.ComponentProps<T>)}
          />
        )
      }}
    />
  )
}

export const FieldController = forwardRef(FieldControllerInner) as <T extends React.ElementType>(
  props: FieldControllerProps<T> & { ref?: React.Ref<HTMLInputElement> }
) => React.ReactElement
